import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { useAuth } from '@/contexts/AuthContext';
import {
  Card,
  Button,
  Input,
  Select,
  Badge,
  Textarea,
  Alert
} from '@/components/ui/StandardComponents';
import { 
  Plus, 
  Trash2, 
  Search, 
  Calculator, 
  Package, 
  Settings,
  Send,
  Zap,
  ArrowRight,
  AlertCircle
} from 'lucide-react';
import type { Client, Equipment, Kit, Service } from '@/types/types';
import { apiFetch } from '@/services/api';
import { useNotifications } from '@/contexts/NotificationContext';
import { BrandLoader } from '@/components/ui/BrandLoader';
import { formatPrice } from '@/utils/formatPrice';
import { toNumber } from '@/utils/typeSafeFormatters';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const ITEM_TYPE_LABELS: Record<string, string> = {
  EQUIPMENT: 'Equipamento',
  KIT: 'Kit',
  SERVICE: 'Serviço',
  CUSTOM: 'Personalizado',
};

const CATALOG_TABS = [
  { value: 'EQUIPMENT', label: 'Equipamentos' },
  { value: 'KIT', label: 'Kits' },
  { value: 'SERVICE', label: 'Serviços' },
] as const;

const PAGE_TITLE = 'Proposta Rápida';
const BREADCRUMBS = [
  { name: 'Admin', href: '/admin/painel' },
  { name: 'Reservas', href: '/admin/reservas' },
  { name: 'Nova proposta' },
];

// Mesma geração de chave do apiFetch (src/services/api.ts).
const newIdempotencyKey = (): string =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : Date.now().toString(36) + Math.random().toString(36).slice(2, 9);

interface ProposalItem {
  id: string;
  type: 'KIT' | 'EQUIPMENT' | 'SERVICE' | 'CUSTOM';
  sourceId?: string;
  description: string;
  quantity: number;
  unitPrice: number;
  discount: number;
  totalPrice: number;
  imageUrl?: string;
}

export const AdminQuickProposalPage = () => {
  const navigate = useNavigate();
  const { addNotification } = useNotifications();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [sendingWhatsApp, setSendingWhatsApp] = useState(false);

  // Form State - Cliente
  const [clientType, setClientType] = useState<'registered' | 'manual'>('manual');
  const [selectedClientId, setSelectedClientId] = useState('');
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [clientContact, setClientContact] = useState('');
  // Erros de campos (escopo global)
  const [fieldErrors] = useState<Record<string, string>>({});

  // Form State - Evento
  const [eventTitle, setEventTitle] = useState('');
  const [location, setLocation] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [eventEndDate, setEventEndDate] = useState('');
  const [notes, setNotes] = useState('');

  // Form State - Endereço (obrigatórios pelo backend)
  const [street, setStreet] = useState('');
  const [addressNumber, setAddressNumber] = useState('');
  const [neighborhood, setNeighborhood] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zipCode, setZipCode] = useState('');

  // Items State
  const [items, setItems] = useState<ProposalItem[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [equipments, setEquipments] = useState<Equipment[]>([]);
  const [kits, setKits] = useState<Kit[]>([]);
  const [services, setServices] = useState<Service[]>([]);

  // Catalog Search
  const [searchType, setSearchType] = useState<'EQUIPMENT' | 'KIT' | 'SERVICE'>('EQUIPMENT');
  const [searchTerm, setSearchTerm] = useState('');
  // Falha ao carregar o catálogo virava só um toast e a tela seguia com o catálogo vazio.
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  // Achado (revisão antes do deploy): cada clique em "Gerar link da proposta" mandava uma
  // Idempotency-Key nova, então tentar de novo depois de um erro podia gravar a reserva duas
  // vezes. O mesmo envio (mesmo corpo) repete a chave e o backend devolve a reserva já gravada;
  // se o formulário mudou, é outro envio e ganha chave nova.
  const lastSubmissionRef = useRef<{ body: string; key: string } | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setCatalogError(null);
        const [clientsRes, equipsRes, kitsRes, servicesRes] = await Promise.all([
          apiFetch<Client[]>('/admin/clients'),
          apiFetch<Equipment[]>('/equipments'),
          apiFetch<Kit[]>('/kits'),
          apiFetch<Service[]>('/services')
        ]);
        setClients(Array.isArray(clientsRes) ? clientsRes : []);
        setEquipments(Array.isArray(equipsRes) ? equipsRes : []);
        setKits(Array.isArray(kitsRes) ? kitsRes : []);
        setServices(Array.isArray(servicesRes) ? servicesRes : []);
      } catch (error: unknown) {
        const err = error as { message?: string } | null;
        setCatalogError(err?.message || 'Não foi possível carregar clientes e catálogo.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [reloadKey]);

  const subtotal = useMemo(() => {
    return items.reduce((sum, item) => sum + toNumber(item.totalPrice), 0);
  }, [items]);

  const filteredCatalogItems = useMemo(() => {
    const source = searchType === 'EQUIPMENT' ? equipments : searchType === 'KIT' ? kits : services;
    if (!searchTerm.trim()) return source;
    const lower = searchTerm.toLowerCase();
    return source.filter((item: Equipment | Kit | Service) => item.name?.toLowerCase().includes(lower));
  }, [searchType, searchTerm, equipments, kits, services]);

  const addItemToProposal = (type: ProposalItem['type'], item: Equipment | Kit | Service) => {
    const newItem: ProposalItem = {
      id: Math.random().toString(36).substr(2, 9),
      type,
      sourceId: item.id,
      description: item.name,
      quantity: 1,
      unitPrice: Number((item as Equipment).dailyPrice || (item as Kit | Service).price || 0),
      discount: 0,
      totalPrice: Number((item as Equipment).dailyPrice || (item as Kit | Service).price || 0),
      imageUrl: item.imageUrl
    };
    setItems(prev => [...prev, newItem]);
    addNotification({ type: 'success', title: 'Item adicionado', message: item.name });
  };

  const addCustomItem = () => {
    const newItem: ProposalItem = {
      id: Math.random().toString(36).substr(2, 9),
      type: 'CUSTOM',
      description: 'Novo Serviço/Item',
      quantity: 1,
      unitPrice: 0,
      discount: 0,
      totalPrice: 0
    };
    setItems(prev => [...prev, newItem]);
  };

  const updateItem = (id: string, field: keyof ProposalItem, value: string | number) => {
    setItems(prev => prev.map(item => {
      if (item.id !== id) return item;
      const updated = { ...item, [field]: value };
      updated.totalPrice = (updated.unitPrice * updated.quantity) - updated.discount;
      return updated;
    }));
  };

  const removeItem = (id: string) => {
    setItems(items.filter(i => i.id !== id));
  };

  const validate = (): string | null => {
    if (items.length === 0) return 'Adicione pelo menos um item ao orçamento.';
    if (!eventDate) return 'Informe a data de início do evento.';
    if (!eventEndDate) return 'Informe a data de término do evento.';
    if (new Date(eventEndDate) <= new Date(eventDate)) return 'A data de término deve ser posterior à data de início.';
    if (!location.trim()) return 'Informe o local do evento.';
    if (!street.trim()) return 'Informe a rua do evento.';
    if (!addressNumber.trim()) return 'Informe o número do endereço.';
    if (!neighborhood.trim()) return 'Informe o bairro.';
    if (!city.trim()) return 'Informe a cidade.';
    if (!state.trim()) return 'Informe o estado (UF).';
    if (!zipCode.trim()) return 'Informe o CEP.';
    if (clientType === 'manual') {
      if (!clientName.trim()) return 'Informe o nome do cliente.';
      if (!clientContact.trim()) return 'Informe o contato (WhatsApp) do cliente.';
    } else if (clientType === 'registered' && !selectedClientId) {
      return 'Selecione um cliente cadastrado.';
    }
    // Garantir que há pelo menos um item de catálogo (schema exige kitId ou equipmentIds ou serviceIds)
    const hasCatalogItem = items.some(i => i.type !== 'CUSTOM' && i.sourceId);
    if (!hasCatalogItem) return 'Adicione pelo menos um equipamento, kit ou serviço do catálogo.';
    return null;
  };

  const handleSave = async (status: 'DRAFT' | 'PENDING') => {
    const validationError = validate();
    if (validationError) {
      addNotification({ type: 'error', title: 'Campo obrigatório', message: validationError });
      return;
    }

    try {
      setSaving(true);

      // Extrair IDs de cada tipo de item (como o backend espera)
      const equipmentIds = items.filter(i => i.type === 'EQUIPMENT' && i.sourceId).map(i => i.sourceId!);
      const serviceIds = items.filter(i => i.type === 'SERVICE' && i.sourceId).map(i => i.sourceId!);
      const kitIdItem = items.find(i => i.type === 'KIT' && i.sourceId);

      // Itens CUSTOM entram nas observações
      const customItemsText = items
        .filter(i => i.type === 'CUSTOM')
        .map(i => `• ${i.description} (R$ ${i.unitPrice} x ${i.quantity})`)
        .join('\n');
      const fullNotes = customItemsText ? `${notes}\n\nItens personalizados:\n${customItemsText}`.trim() : notes;

      // Achado (auditoria ponta a ponta): ia também `userId: user?.id`, o id do admin logado. Com
      // cliente manual, o backend pendurava a reserva no perfil de cliente do próprio admin (telefone
      // da primeira proposta manual) e a confirmação ia ao admin por e-mail e a outro cliente por
      // WhatsApp. O cliente é o cadastrado (clientId) ou o manual, gravado na própria reserva.
      const payload: Record<string, unknown> = {
        clientId: clientType === 'registered' ? selectedClientId : undefined,
        clientName: clientType === 'manual' ? clientName : undefined,
        clientContact: clientType === 'manual' ? clientContact : undefined,
        // Achado (revisão antes do deploy): o e-mail é opcional, mas '' ia no corpo e o backend
        // (z.string().email().optional()) respondia 400 — proposta sem e-mail nunca era criada.
        clientEmail: clientType === 'manual' ? clientEmail.trim() || undefined : undefined,
        eventTitle: eventTitle || 'Proposta Rápida',
        location,
        street,
        addressNumber,
        neighborhood,
        city,
        state,
        zipCode,
        eventDate: new Date(eventDate).toISOString(),
        eventEndDate: new Date(eventEndDate).toISOString(),
        notes: fullNotes,
        totalPrice: subtotal,
        status,
        ...(equipmentIds.length > 0 && { equipmentIds }),
        ...(serviceIds.length > 0 && { serviceIds }),
        ...(kitIdItem?.sourceId && { kitId: kitIdItem.sourceId }),
      };

      const body = JSON.stringify(payload);
      const previous = lastSubmissionRef.current;
      const idempotencyKey = previous && previous.body === body ? previous.key : newIdempotencyKey();
      lastSubmissionRef.current = { body, key: idempotencyKey };

      // Achado (revisão antes do deploy): o apiFetch já desembrulha { success, data } e devolve a
      // própria reserva; ler `.data.id` dava sempre vazio, e toda criação bem-sucedida terminava
      // em "Reserva criada mas ID não retornado.".
      const booking = await apiFetch<{ id: string }>('/bookings', {
        method: 'POST',
        body,
        headers: { 'Idempotency-Key': idempotencyKey }
      });

      const bookingId = booking?.id;
      if (!bookingId) throw new Error('Reserva criada mas ID não retornado.');

      addNotification({ type: 'success', title: 'Proposta gerada!', message: 'O link de proposta está disponível na página da reserva.' });
      navigate(`/admin/reservas/${bookingId}`);
    } catch (error: unknown) {
      const err = error as { message?: string } | null;
      const msg = err?.message || 'Falha ao salvar. Verifique os campos e tente novamente.';
      addNotification({ type: 'error', title: 'Erro ao gerar proposta', message: msg });
    } finally {
      setSaving(false);
    }
  };

  const formatDateTime = (value: string): string => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString('pt-BR');
  };

  const loadLogoAsDataUrl = async (): Promise<string | null> => {
    try {
      const response = await fetch('/xproducoes-logo.png');
      if (!response.ok) return null;
      const blob = await response.blob();
      return await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error('Falha ao converter logo em base64'));
        reader.readAsDataURL(blob);
      });
    } catch {
      return null;
    }
  };

  const getClientDisplayName = (): string => {
    if (clientType === 'registered') {
      const found = clients.find((c) => c.id === selectedClientId);
      return found?.user?.name || found?.name || 'Cliente cadastrado';
    }
    return clientName || 'Cliente não informado';
  };

  const getClientPhone = (): string => {
    if (clientType === 'registered') {
      const found = clients.find((c) => c.id === selectedClientId);
      return found?.phone || found?.user?.phone || '';
    }
    return clientContact || '';
  };

  const getClientEmail = (): string => {
    if (clientType === 'registered') {
      const found = clients.find((c) => c.id === selectedClientId);
      return found?.email || found?.user?.email || '';
    }
    return clientEmail || '';
  };

  const getRegistrationUrl = (): string => {
    if (typeof window === 'undefined') return 'https://xproducoeseeventos.com.br/cadastro';
    return `${window.location.origin}/cadastro`;
  };

  const buildWhatsappInviteText = (): string => {
    const eventName = eventTitle || 'seu evento';
    const registrationUrl = getRegistrationUrl();
    const progressUrl = typeof window !== 'undefined' ? `${window.location.origin}/cliente/painel` : 'https://xproducoeseeventos.com.br/cliente/painel';
    return [
      `Olá! A proposta comercial do evento "${eventName}" está pronta.` ,
      `Total estimado: ${formatPrice(subtotal)}.`,
      'Acabei de gerar o PDF para envio neste atendimento.',
      `Para acompanhar o progresso da reserva em tempo real, acesse: ${progressUrl}`,
      `Se ainda não tiver cadastro, crie aqui: ${registrationUrl}`,
      'Assim que concluir, nossa equipe vincula sua proposta ao seu acesso.',
    ].join(' ');
  };

  const normalizeWhatsAppNumber = (raw: string): string | null => {
    const digits = raw.replace(/\D/g, '');
    if (!digits) return null;

    if (digits.startsWith('55') && digits.length >= 12) return digits;
    if (digits.length === 10 || digits.length === 11) return `55${digits}`;
    if (digits.length >= 12) return digits;

    return null;
  };

  const generateProposalPdf = async (): Promise<void> => {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const logoDataUrl = await loadLogoAsDataUrl();

    if (logoDataUrl) {
      doc.addImage(logoDataUrl, 'PNG', 14, 10, 42, 14);
    }

    doc.setFontSize(16);
    doc.text('Proposta Comercial', 14, 32);

    doc.setFontSize(10);
    doc.text(`Gerada em: ${new Date().toLocaleString('pt-BR')}`, 14, 38);
    doc.text(`Responsavel: ${user?.name || 'Equipe X Producoes'}`, 14, 43);

    doc.setFontSize(11);
    doc.text('Cliente', 14, 52);
    doc.setFontSize(10);
    doc.text(`Nome: ${getClientDisplayName()}`, 14, 57);
    doc.text(`WhatsApp: ${getClientPhone() || '-'}`, 14, 62);
    doc.text(`Email: ${getClientEmail() || '-'}`, 14, 67);

    doc.setFontSize(11);
    doc.text('Evento', 14, 77);
    doc.setFontSize(10);
    doc.text(`Titulo: ${eventTitle || 'Proposta Rapida'}`, 14, 82);
    doc.text(`Inicio: ${formatDateTime(eventDate)}`, 14, 87);
    doc.text(`Termino: ${formatDateTime(eventEndDate)}`, 14, 92);
    doc.text(`Local: ${location || '-'}`, 14, 97);

    const fullAddress = [street, addressNumber, neighborhood, city, state, zipCode]
      .filter(Boolean)
      .join(', ');
    const addressLines = doc.splitTextToSize(`Endereco: ${fullAddress || '-'}`, 180);
    doc.text(addressLines, 14, 102);

    autoTable(doc, {
      startY: 112,
      head: [['Item', 'Tipo', 'Qtd', 'Unitario', 'Desconto', 'Total']],
      body: items.map((item) => [
        item.description,
        item.type,
        String(item.quantity),
        formatPrice(item.unitPrice),
        formatPrice(item.discount),
        formatPrice(item.totalPrice),
      ]),
      styles: { fontSize: 9, cellPadding: 2 },
      headStyles: { fillColor: [25, 118, 210] },
    });

    const tableEndY = (doc as jsPDF & { lastAutoTable?: { finalY?: number } }).lastAutoTable?.finalY || 120;
    doc.setFontSize(12);
    doc.text(`Total estimado: ${formatPrice(subtotal)}`, 14, tableEndY + 10);

    doc.setFontSize(10);
    const notesText = notes?.trim() ? notes.trim() : 'Sem observacoes internas.';
    const notesLines = doc.splitTextToSize(`Observacoes: ${notesText}`, 180);
    doc.text(notesLines, 14, tableEndY + 18);

    const inviteTitleY = tableEndY + 34;
    doc.setFontSize(11);
    doc.text('Mensagem sugerida para WhatsApp', 14, inviteTitleY);
    doc.setFontSize(10);
    const inviteLines = doc.splitTextToSize(buildWhatsappInviteText(), 180);
    doc.text(inviteLines, 14, inviteTitleY + 6);

    const filenameClient = getClientDisplayName().replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase();
    const fileName = `proposta-${filenameClient || 'cliente'}-${Date.now()}.pdf`;
    doc.save(fileName);
  };

  const handleDownloadPdf = async () => {
    const validationError = validate();
    if (validationError) {
      addNotification({ type: 'error', title: 'Campos pendentes', message: validationError });
      return;
    }

    try {
      setGeneratingPdf(true);
      await generateProposalPdf();

      addNotification({
        type: 'success',
        title: 'PDF pronto',
        message: 'Arquivo gerado com sucesso para envio via WhatsApp.',
      });
    } catch (error: unknown) {
      const err = error as { message?: string } | null;
      addNotification({
        type: 'error',
        title: 'Falha no PDF',
        message: err?.message || 'Nao foi possivel gerar o PDF da proposta.',
      });
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleDownloadAndOpenWhatsApp = async () => {
    const validationError = validate();
    if (validationError) {
      addNotification({ type: 'error', title: 'Campos pendentes', message: validationError });
      return;
    }

    const normalizedPhone = normalizeWhatsAppNumber(getClientPhone());
    if (!normalizedPhone) {
      addNotification({
        type: 'error',
        title: 'WhatsApp inválido',
        message: 'Informe um telefone de cliente válido para abrir o WhatsApp automaticamente.',
      });
      return;
    }

    try {
      setSendingWhatsApp(true);
      await generateProposalPdf();

      const whatsappText = buildWhatsappInviteText();
      const waUrl = `https://wa.me/${normalizedPhone}?text=${encodeURIComponent(whatsappText)}`;
      window.open(waUrl, '_blank', 'noopener,noreferrer');

      addNotification({
        type: 'success',
        title: 'WhatsApp aberto',
        message: 'PDF baixado. O WhatsApp foi aberto com mensagem pronta. Anexe o PDF na conversa para enviar.',
      });
    } catch (error: unknown) {
      const err = error as { message?: string } | null;
      addNotification({
        type: 'error',
        title: 'Falha no envio',
        message: err?.message || 'Nao foi possivel abrir o WhatsApp com a proposta.',
      });
    } finally {
      setSendingWhatsApp(false);
    }
  };

  // Carregando dentro do layout (antes: BrandLoader em tela cheia, sem menu nem título).
  if (loading) {
    return (
      <AdminLayout title={PAGE_TITLE} breadcrumbs={BREADCRUMBS}>
        <div className="flex items-center justify-center min-h-[60vh]">
          <BrandLoader size="xl" label="Carregando catálogo…" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title={PAGE_TITLE} breadcrumbs={BREADCRUMBS}>
      <div className="max-w-6xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">

        <header className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-2xl bg-primary/10 border border-primary/20">
              <Zap className="h-6 w-6 text-primary" aria-hidden="true" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-foreground">Nova proposta comercial</h2>
              <p className="text-sm text-muted-foreground">Monte o orçamento e gere o link ou o PDF para o cliente.</p>
            </div>
          </div>
          {/* flex-wrap: os 4 botões em linha estouravam a tela de 375px. */}
          <div className="flex flex-wrap gap-2">
             <Button variant="outline" size="sm" onClick={() => navigate(-1)}>Cancelar</Button>
             <Button variant="outline" size="sm" onClick={handleDownloadPdf} isLoading={generatingPdf}>
              Baixar PDF
             </Button>
             <Button variant="outline" size="sm" onClick={handleDownloadAndOpenWhatsApp} isLoading={sendingWhatsApp}>
              PDF + WhatsApp
             </Button>
             <Button size="sm" onClick={() => handleSave('PENDING')} isLoading={saving} leftIcon={<Send className="h-4 w-4" />}>
                Gerar link da proposta
             </Button>
          </div>
        </header>

        {catalogError && (
          <Alert
            variant="error"
            title="Erro ao carregar o catálogo"
            description={catalogError}
            action={<Button variant="outline" size="sm" onClick={() => setReloadKey((k) => k + 1)}>Tentar de novo</Button>}
          />
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Form Area */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Client Card */}
            <Card className="p-6 border-2">
               <h3 className="flex items-center gap-2 mb-6 text-primary font-semibold uppercase text-xs tracking-wider">
                 <Package className="h-4 w-4" aria-hidden="true" /> Dados do cliente
               </h3>
               
               <div className="space-y-4">
                  <Select 
                    label="Origem do Cliente"
                    value={clientType}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setClientType(e.target.value as 'manual' | 'registered')}
                    options={[
                      { value: 'manual', label: 'Manual (Conversa do WhatsApp)' },
                      { value: 'registered', label: 'Base de Clientes Cadastrados' }
                    ]}
                  />

                  {clientType === 'registered' ? (
                    <Select 
                      label="Selecionar Cliente"
                      value={selectedClientId}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSelectedClientId(e.target.value)}
                      options={[{ value: '', label: 'Selecione...' }, ...clients.map(c => ({ value: c.id, label: c.user?.name || c.name }))]}
                    />
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Input label="Nome Completo *" value={clientName} onChange={e => setClientName(e.target.value)} placeholder="Ex: João Silva" />
                      <Input label="WhatsApp / Celular *" value={clientContact} onChange={e => setClientContact(e.target.value)} placeholder="(00) 00000-0000" />
                      <Input label="E-mail" value={clientEmail} onChange={e => setClientEmail(e.target.value)} placeholder="Ex: joao@email.com" type="email" />
                    </div>
                  )}
               </div>
            </Card>

            {/* Items Card */}
            <Card className="p-6 border-2 border-primary/10 overflow-hidden">
               <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                 <h3 className="flex items-center gap-2 text-primary font-semibold uppercase text-xs tracking-wider">
                   <Calculator className="h-4 w-4" aria-hidden="true" /> Itens do orçamento
                 </h3>
                 <Button variant="outline" size="xs" onClick={addCustomItem} leftIcon={<Plus className="h-3 w-3" />}>
                   Item personalizado
                 </Button>
               </div>

               <div className="space-y-4">
                 {items.length === 0 ? (
                   <div className="py-12 text-center border-2 border-dashed rounded-2xl border-border bg-muted/20">
                      <p className="text-muted-foreground text-sm">Nenhum item adicionado ainda.</p>
                      <p className="text-sm text-muted-foreground">Use o catálogo ao lado para começar.</p>
                   </div>
                 ) : (
                   <div className="space-y-3">
                     {items.map((item) => (
                       <div key={item.id} className="group relative flex flex-col md:flex-row gap-4 p-4 rounded-xl border border-border bg-card hover:border-primary/30 transition-all hover:shadow-md">
                         <div className="flex-grow space-y-2">
                           <Input
                             size="xs"
                             aria-label="Descrição do item"
                             className="font-bold text-sm bg-transparent"
                             value={item.description}
                             onChange={e => updateItem(item.id, 'description', e.target.value)}
                           />
                           <div className="flex items-center gap-2">
                              <Badge variant="outline" size="sm">
                                {ITEM_TYPE_LABELS[item.type] || item.type}
                              </Badge>
                           </div>
                         </div>

                         <div className="flex flex-wrap items-end gap-3">
                           <div className="w-20">
                             <Input
                               label="Qtd"
                               size="xs"
                               type="number"
                               className="text-center font-bold"
                               value={item.quantity}
                               onChange={e => updateItem(item.id, 'quantity', Number(e.target.value))}
                             />
                           </div>
                           <div className="w-36">
                             {/* `prefix="R$"` não é prop do Input (virava atributo HTML sem efeito). */}
                             <Input
                               label="Preço unit."
                               size="xs"
                               type="number"
                               leftIcon={<span className="text-xs font-semibold">R$</span>}
                               value={item.unitPrice}
                               onChange={e => updateItem(item.id, 'unitPrice', Number(e.target.value))}
                             />
                           </div>
                           <Button variant="ghost" size="icon" onClick={() => removeItem(item.id)} className="text-destructive hover:bg-destructive/10 h-8 w-8" aria-label={`Remover ${item.description || 'item'}`}>
                             <Trash2 className="h-4 w-4" />
                           </Button>
                         </div>
                       </div>
                     ))}
                   </div>
                 )}
               </div>

               <div className="mt-8 pt-6 border-t space-y-2">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-muted-foreground font-medium uppercase tracking-wider text-xs">Subtotal estimado</span>
                    <span className="font-mono font-bold text-lg">{formatPrice(subtotal)}</span>
                  </div>
               </div>
            </Card>

            <Card className="p-6">
               <h3 className="flex items-center gap-2 mb-6 text-primary font-semibold uppercase text-xs tracking-wider">
                 <Settings className="h-4 w-4" aria-hidden="true" /> Evento e local
               </h3>
               <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                 <Input label="Título do Evento" value={eventTitle} onChange={e => setEventTitle(e.target.value)} placeholder="Ex: Casamento João & Maria" />
                 <Input label="Local / Salão *" value={location} onChange={e => setLocation(e.target.value)} placeholder="Ex: Espaço VIP" />
                 <Input label="Início Previsto *" type="datetime-local" value={eventDate} onChange={e => setEventDate(e.target.value)} />
                 <Input label="Término Previsto *" type="datetime-local" value={eventEndDate} onChange={e => setEventEndDate(e.target.value)} />
               </div>

               <div className="mt-4 rounded-xl border border-border bg-muted/30 p-4 space-y-3">
                 <h4 className="flex items-center gap-2 text-muted-foreground font-semibold uppercase text-xs tracking-wider">
                   <AlertCircle className="h-3 w-3" aria-hidden="true" /> Endereço do evento (obrigatório)
                 </h4>
                 <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                   <Input label="CEP *" value={zipCode} onChange={e => setZipCode(e.target.value)} placeholder="00000-000" className="md:col-span-1" error={fieldErrors.zipCode} />
                   <Input label="Rua *" value={street} onChange={e => setStreet(e.target.value)} placeholder="Ex: Av. Brasil" className="md:col-span-2" error={fieldErrors.street} />
                 </div>
                 <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                   <Input label="Número *" value={addressNumber} onChange={e => setAddressNumber(e.target.value)} placeholder="Ex: 123" error={fieldErrors.addressNumber} />
                   <Input label="Bairro *" value={neighborhood} onChange={e => setNeighborhood(e.target.value)} placeholder="Ex: Centro" error={fieldErrors.neighborhood} />
                   <Input label="Cidade *" value={city} onChange={e => setCity(e.target.value)} placeholder="Ex: Belo Horizonte" error={fieldErrors.city} />
                   <Input label="UF *" value={state} onChange={e => setState(e.target.value)} placeholder="Ex: MG" maxLength={2} error={fieldErrors.state} />
                 </div>
               </div>
               <div className="mt-4">
                 <Textarea label="Observações Internas (Não aparecem na proposta)" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Ex.: cliente pediu desconto; negociar o frete se fechar hoje." />
               </div>
            </Card>
          </div>

          {/* Catalog Sidebar */}
          <div className="space-y-6">
             <Card className="p-4 border-primary/20 bg-primary/5 sticky top-24">
                <h3 className="flex items-center gap-2 mb-4 text-primary font-semibold uppercase text-xs tracking-wider">
                  <Search className="h-4 w-4" aria-hidden="true" /> Adicionar do catálogo
                </h3>

                <div className="flex gap-1 p-1 bg-muted/30 rounded-xl mb-4" role="group" aria-label="Tipo de item">
                   {CATALOG_TABS.map(tab => (
                     <Button
                       key={tab.value}
                       size="xs"
                       variant={searchType === tab.value ? 'primary' : 'ghost'}
                       aria-pressed={searchType === tab.value}
                       className="flex-1 px-2"
                       onClick={() => setSearchType(tab.value)}
                     >
                       {tab.label}
                     </Button>
                   ))}
                </div>

                <div className="mb-4">
                  <Input
                    size="sm"
                    aria-label="Buscar no catálogo"
                    leftIcon={<Search className="h-4 w-4" />}
                    placeholder="Buscar..."
                    className="bg-background"
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                  />
                </div>

                <div className="space-y-2 max-h-[500px] overflow-y-auto pr-2">
                  {filteredCatalogItems.length === 0 && (
                    <p className="text-center text-xs text-muted-foreground py-6">Nenhum item encontrado.</p>
                  )}
                  {filteredCatalogItems.map((item: Equipment | Kit | Service) => (
                      <button
                        type="button"
                        key={item.id}
                        aria-label={`Adicionar ${item.name}`}
                        className="group w-full text-left p-3 rounded-xl border border-border bg-card hover:border-primary/40 transition-all hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        onClick={() => addItemToProposal(searchType, item)}
                      >
                        <div className="flex justify-between items-start gap-2">
                           <div className="min-w-0">
                             <p className="font-semibold text-sm text-foreground truncate group-hover:text-primary transition-colors">{item.name}</p>
                             <p className="text-xs text-muted-foreground font-mono">
                               {formatPrice(Number(((item as Equipment).dailyPrice || (item as Kit | Service).price) || 0))}
                             </p>
                           </div>
                           <div className="h-6 w-6 shrink-0 rounded-lg bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground flex items-center justify-center transition-all" aria-hidden="true">
                              <ArrowRight className="h-3 w-3" />
                           </div>
                        </div>
                      </button>
                  ))}
                </div>
             </Card>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminQuickProposalPage;
